<script setup>
import Footer from '@/components/Footer.vue';
import Button from '@/components/ui/button/Button.vue';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu';
import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { useDarkModeStore } from '@/stores/dark-mode';
import { usePage } from '@inertiajs/vue3';
import { ChevronDown, LogOut } from 'lucide-vue-next';
import { storeToRefs } from 'pinia';
import { onMounted, ref } from 'vue';
import { route } from 'ziggy-js';

const page = usePage();
console.log(page.props);
const isAdmin = ref(false); // Track if the user is an admin
const siteInfo = ref(page.props.siteInformation);
onMounted(async () => {
    if (page.props?.auth?.user) {
        if (page.props?.auth?.roles[0] === 'Super Admin') {
            isAdmin.value = true;
        } else {
            isAdmin.value = false;
        }
    }
});

const mainMenu = [
    { name: 'Ana Sayfa', url: route('home'), match: ['/dashboard', '/'] },
    { name: 'Tüm İlanlarımız', url: route('all-cars'), match: ['/tum-ilanlar'] },
    { name: 'Ekibimiz', url: route('teams'), match: ['/ekibimiz'] },
    { name: 'Aracını Bize Sat', url: route('sell-cars.create'), match: ['/sell-cars/create'] },
    { name: 'Hakkımızda', url: route('about'), match: ['/hakkimizda'] },
    { name: 'İletişim', url: route('contact'), match: ['/iletisim'] },
];

const mainMenuAdmin = [
    { name: 'İlanlar', url: route('cars.index'), match: ['/cars'] },
    { name: 'Aracını Bize Sat', url: route('sell-cars.index'), match: ['/sell-cars'] },
    { name: 'Ekip Üyeleri', url: route('teamers.index'), match: ['/teamers'] },
    { name: 'Blog', url: route('blogs.index'), match: ['/blogs'] },
    { name: 'Ayarlar', url: route('site-informations.edit'), match: ['/site-informations/edit'] },
    { name: 'Dosyalar', url: route('attachments.index'), match: ['/attachments'] },
];

// Check if any admin menu item is active
const isAdminMenuActive = () => {
    return mainMenuAdmin.some((item) => item.match.some((match) => page.url.includes(match)));
};

const { darkMode } = storeToRefs(useDarkModeStore());

function logout() {
    window.axios
        .post('/logout')
        .then(() => {
            window.location.href = '/login';
        })
        .catch((error) => {
            console.error('Logout işlemi başarısız:', error);
        });
}

const mobileMenuOpen = ref(false); // Mobil menü açık mı?

function toggleMobileMenu() {
    mobileMenuOpen.value = !mobileMenuOpen.value;
}
function closeMobileMenu() {
    mobileMenuOpen.value = false;
}
</script>

<template>
    <div class="flex min-h-screen flex-col bg-[#f7f9fb] dark:bg-gray-900">
        <!-- changed: added flex flex-col -->
        <!-- Header -->
        <header class="bg-[#e6e8e9] py-2 shadow-lg">
            <div class="z-30 mx-auto flex w-full max-w-7xl items-center justify-between px-4 md:px-0 lg:px-0">
                <!-- Logo & Brand -->
                <div class="flex items-center gap-3">
                    <a href="/" class="flex items-center gap-2 text-2xl font-bold tracking-tight text-[#1a2238] md:text-3xl">
                        <template v-if="siteInfo.is_logo_image && siteInfo.logo_image_path">
                            <img
                                :src="siteInfo.logo_image_path"
                                alt="Logo"
                                class="object-contain"
                                :style="{
                                    height: siteInfo.logo_height ? siteInfo.logo_height + 'px' : null,
                                    width: siteInfo.logo_width ? siteInfo.logo_width + 'px' : 'auto',
                                }"
                            />
                        </template>
                        <template v-else>
                            {{ siteInfo.logo }}
                        </template>
                    </a>
                </div>
                <!-- Main Menu (Desktop) -->
                <nav class="hidden gap-8 md:flex" :class="{ 'ml-auto justify-end': !isAdmin }">
                    <a v-for="item in mainMenu" :key="item.name" :href="item.url" class="relative px-2 py-1 font-semibold text-[#1a2238] transition-colors duration-200 hover:text-blue-600" :class="{ 'text-text-blue-600 after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:w-full after:rounded after:bg-blue-600': item.match.includes(page.url) }">
                        {{ item.name }}
                    </a>
                    <!-- Admin Dropdown -->
                    <template v-if="isAdmin">
                        <DropdownMenu>
                            <DropdownMenuTrigger as-child>
                                <button class="flex items-center gap-1 px-2 py-1 font-semibold text-[#1a2238] transition-colors duration-200 hover:text-blue-600" :class="{ 'text-text-blue-600 after:absolute after:-bottom-1 after:left-0 after:h-0.5 after:w-full after:rounded': isAdminMenuActive() }">
                                    Admin
                                    <ChevronDown class="h-4 w-4" />
                                </button>
                            </DropdownMenuTrigger>
                            <DropdownMenuContent class="w-48">
                                <DropdownMenuItem v-for="item in mainMenuAdmin" :key="item.name" as-child>
                                    <a :href="item.url" class="w-full cursor-pointer">
                                        {{ item.name }}
                                    </a>
                                </DropdownMenuItem>
                            </DropdownMenuContent>
                        </DropdownMenu>
                    </template>
                </nav>
                <!-- Dark Mode & User Actions (Desktop) -->
                <div class="flex items-center gap-2">
                    <!-- Mobile Menu Button -->
                    <Button variant="action" class="md:hidden" @click="toggleMobileMenu">
                        <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M4 6h16M4 12h16M4 18h16" />
                        </svg>
                        <span class="sr-only">Menü</span>
                    </Button>

                    <div v-if="page.props.auth.user">
                        <Button variant="action" @click="logout">
                            <LogOut />
                            <span class="sr-only">Logout</span>
                        </Button>
                    </div>
                    <!-- User Avatar & Dropdown (Desktop) -->
                    <div v-if="isAdmin" class="hidden items-center gap-4 md:flex">
                        <HoverCard>
                            <HoverCardTrigger as-child>
                                <div class="flex cursor-pointer items-center gap-2">
                                    <span class="hidden font-medium text-[#1a2238] md:block">{{ page.props.auth.user.name }}</span>
                                </div>
                            </HoverCardTrigger>
                            <HoverCardContent class="min-w-[160px]">
                                <div class="flex flex-col gap-1">
                                    <a href="/profile" class="block rounded px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Profil</a>
                                    <button @click="logout" class="w-full rounded px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700">Çıkış Yap</button>
                                </div>
                            </HoverCardContent>
                        </HoverCard>
                    </div>
                </div>
            </div>
        </header>

        <!-- Mobile Menu Panel -->
        <transition name="fade">
            <div v-if="mobileMenuOpen" class="fixed inset-0 z-40 bg-black/40 md:hidden" @click.self="closeMobileMenu">
                <div class="animate-slide-in absolute right-0 top-0 flex h-full w-80 flex-col gap-4 bg-white p-6 shadow-xl dark:bg-gray-900">
                    <div class="mb-4 flex items-center justify-between">
                        <span class="text-xl font-bold text-[#1a2238] dark:text-white">Menü</span>
                        <button @click="closeMobileMenu" class="rounded-lg p-2 text-gray-700 transition-colors hover:bg-gray-200 dark:text-white dark:hover:bg-gray-800">
                            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>
                    </div>
                    <nav class="flex flex-col gap-1">
                        <a v-for="item in mainMenu" :key="item.name" :href="item.url" @click="closeMobileMenu" class="rounded-lg px-3 py-2 font-semibold text-[#1a2238] transition-colors hover:bg-[#ff6a3d]/10 dark:text-white" :class="{ 'bg-[#ff6a3d]/10 text-[#ff6a3d]': item.match.includes(page.url) }">
                            {{ item.name }}
                        </a>
                        <!-- Admin Section in Mobile Menu -->
                        <template v-if="isAdmin">
                            <div class="mt-4 border-t pt-4">
                                <div class="mb-2 font-semibold text-[#1a2238] dark:text-white">Admin</div>
                                <a v-for="item in mainMenuAdmin" :key="item.name" :href="item.url" @click="closeMobileMenu" class="block rounded-lg px-3 py-2 font-semibold text-[#1a2238] transition-colors hover:bg-[#ff6a3d]/10 dark:text-white" :class="{ 'bg-[#ff6a3d]/10 text-[#ff6a3d]': item.match.includes(page.url) }">
                                    {{ item.name }}
                                </a>
                            </div>
                        </template>
                    </nav>

                    <div v-if="isAdmin" class="mt-4 flex flex-col gap-2 border-t pt-4">
                        <span class="font-medium text-[#1a2238] dark:text-white">{{ page.props.auth.user.name }}</span>
                        <a href="/profile" @click="closeMobileMenu" class="block rounded px-4 py-2 text-sm hover:bg-gray-100 dark:hover:bg-gray-700">Profil</a>
                        <button
                            @click="
                                () => {
                                    logout();
                                    closeMobileMenu();
                                }
                            "
                            class="w-full rounded px-4 py-2 text-left text-sm text-red-600 hover:bg-gray-100 dark:hover:bg-gray-700"
                        >
                            Çıkış Yap
                        </button>
                    </div>
                </div>
            </div>
        </transition>
        <!-- Sidebar + Page Content -->
        <div class="flex flex-1">
            <!-- changed: flex-1 for main content wrapper -->
            <!-- Main Content -->
            <main class="mx-auto w-full flex-1 px-4 py-2 md:px-8 md:py-6">
                <!-- flex-1 for main -->
                <slot name="page" />
            </main>
        </div>
        <Footer />
    </div>
</template>

<style scoped>
@keyframes pulse-opacity {
    0%,
    100% {
        opacity: 1;
    }
    50% {
        opacity: 0.3;
    }
}
.pulse-red {
    animation: pulse-opacity 1.5s ease-in-out infinite;
    background-color: #ef4444;
}
@keyframes slide-in {
    0% {
        transform: translateX(100%);
    }
    100% {
        transform: translateX(0);
    }
}
.animate-slide-in {
    animation: slide-in 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}
.fade-enter-active,
.fade-leave-active {
    transition: opacity 0.2s;
}
.fade-enter-from,
.fade-leave-to {
    opacity: 0;
}
</style>
